import { ApolloClient } from 'apollo-client'
import { ApolloCache } from 'apollo-cache'
import { ApolloLink } from 'apollo-link'
import { NormalizedCacheObject } from 'apollo-cache-inmemory'

import { isDeployedInProduction } from 'config'
import { createAugmentedCache, initializeLocalApolloState } from './cache'
import { resolvers } from './dummyData'
import { createCypressLink } from './link/cypressLink'
import { createOnErrorLink } from './link/onError'
import { createConnectionLink } from './link/connection'
import { createClientHeaders } from './link/clientHeaders'
import { createIgnoreSubscriptionsLink } from './link/IgnoreSubscriptionsLink'
import { getUsageMetrics } from 'modules/devtools/usageMetrics/code-split-import'
import isTruthy from 'utils/isTruthy'
import { operationsTimestampLink } from 'modules/analytics'

export interface CreateApolloClientConfig {
  cache?: ApolloCache<NormalizedCacheObject>
  link?: (
    getClient: () => ApolloClient<NormalizedCacheObject>
  ) => ApolloLink | undefined
}

export const createApolloClient = (config: CreateApolloClientConfig = {}) => {
  // Store apollo client on a ref and use it as an argument to avoid circular
  // dependencies by importing this file from other locations inside
  // this directory.
  const clientRef: { ref?: ApolloClient<NormalizedCacheObject> } = {
    ref: undefined,
  }
  const getClient = () => {
    if (
      process.env.NODE_ENV === 'development' ||
      process.env.NODE_ENV === 'test' ||
      process.env.CI === 'true'
    ) {
      if (!clientRef.ref) {
        throw new Error(
          'There is a bug in the code, clientRef.ref should never be undefined at this point. ' +
            'Investigate stack trace to understand where Apollo client is being accessed before it was created'
        )
      }
    }
    return clientRef.ref!
  }

  const usageMetrics = getUsageMetrics()
  const usageMetricsLink =
    usageMetrics.status === 'loaded'
      ? usageMetrics.usageMetrics.apolloLink
      : undefined

  const {
    cache = createAugmentedCache(),
    link = getClient =>
      ApolloLink.from(
        [
          operationsTimestampLink,
          createCypressLink(),
          createClientHeaders(),
          createIgnoreSubscriptionsLink(getClient),
          createOnErrorLink(getClient),
          usageMetricsLink,
          createConnectionLink(getClient, cache),
        ].filter(isTruthy)
      ),
  }: typeof config = config

  initializeLocalApolloState(cache)

  const client = new ApolloClient({
    connectToDevTools: !isDeployedInProduction(),
    link: link(getClient),
    resolvers: resolvers as any,
    cache,
  })

  clientRef.ref = client
  return client
}

export { dataIdFromObject } from './cache'
